// Shared by the drivers list and the Sides filing screen.
//
// Assigning a truck to a driver is ONE operation with one set of rules - who may
// do it, what happens to whoever held the truck before, how that warning is
// surfaced. A second copy on another screen is a second set of rules, and the
// two drift. So this lives here and both screens import it.
import { useState } from 'react';
import { Truck, Loader2, UserCog } from 'lucide-react';
import { DriverSelect, toDriverOption } from './DriverSelect';

export interface PickableVehicle {
  id: string; plate: string; code: string; vehicleType: string;
  heldByDriverId: string | null; heldByDriverName: string | null;
}

// Assigning is role-gated on the server; mirror it here so the cell is a plain
// label for anyone who could not save the change anyway.
export const CAN_ASSIGN = ['super_admin', 'admin', 'accountant', 'site_manager', 'site_supervisor'];

/**
 * The Vehicle cell. Read-only for roles that cannot assign; otherwise a picker
 * that reassigns or clears in place.
 *
 * Defined at module scope on purpose: a component declared inside the page
 * would be a new type on every render and would lose focus and open state each
 * time the list refetched.
 */
export function VehicleCell({ driverId, plate, canAssign, vehicles, onAssign, onUnassign, busy, error }: {
  driverId: string;
  plate: string | null;
  canAssign: boolean;
  vehicles: PickableVehicle[];
  onAssign: (driverId: string, vehicleId: string) => void;
  onUnassign: (driverId: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [open, setOpen] = useState(false);
  // The truck picked that somebody else is holding. Same rule, other direction.
  const [pending, setPending] = useState<PickableVehicle | null>(null);

  if (canAssign && pending) {
    return (
      <div className="space-y-2 min-w-56 rounded-md border border-amber-500/40 bg-amber-500/5 p-2"
           onClick={(e) => e.stopPropagation()}>
        <p className="text-xs text-foreground">
          <b>{pending.plate}</b> is already driven by <b>{pending.heldByDriverName}</b>.
          {' '}Assigning it here takes it off them.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs rounded-md bg-gold/90 hover:bg-gold text-black px-2 py-1"
            onClick={() => { const v = pending; setPending(null); onAssign(driverId, v.id); }}
          >
            Move it here
          </button>
          <button
            type="button"
            className="text-xs underline text-muted-foreground hover:text-foreground"
            onClick={() => setPending(null)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!canAssign) {
    return plate ? (
      <div className="inline-flex items-center gap-1.5 text-xs">
        <Truck className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono" dir="ltr">{plate}</span>
      </div>
    ) : <span className="text-xs text-muted-foreground italic">Not assigned</span>;
  }

  if (!open) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-xs rounded-md border border-transparent hover:border-border px-1.5 py-1 -mx-1.5 transition-colors disabled:opacity-60"
          title={plate ? 'Change the assigned truck' : 'Assign a truck'}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Truck className="h-3 w-3 text-muted-foreground" />}
          {plate
            ? <span className="font-mono" dir="ltr">{plate}</span>
            : <span className="text-muted-foreground italic">Assign…</span>}
        </button>
        {error && <div className="text-[11px] text-destructive">{error}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-1 min-w-52">
      <select
        autoFocus
        className="h-8 w-full rounded-md border border-input bg-background px-1.5 text-xs"
        value=""
        disabled={busy}
        onChange={(e) => {
          const v = e.target.value;
          setOpen(false);
          if (v === '__none__') { onUnassign(driverId); return; }
          if (!v) return;
          const picked = vehicles.find((x) => x.id === v);
          if (picked?.heldByDriverId && picked.heldByDriverId !== driverId) setPending(picked);
          else onAssign(driverId, v);
        }}
        aria-label="Assign a truck"
      >
        <option value="">— pick a truck —</option>
        {plate && <option value="__none__">— none (unassign) —</option>}
        {vehicles.map((v) => (
          <option key={v.id} value={v.id}>
            {v.plate}
            {v.heldByDriverId && v.heldByDriverId !== driverId ? ` · on ${v.heldByDriverName}` : ''}
          </option>
        ))}
      </select>
      <button type="button" className="text-[11px] underline text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(false)}>
        cancel
      </button>
    </div>
  );
}

export interface PickableDriver {
  id: string; code: string; name: string;
  /** Shown in the dropdown: the only thing that separates three men who share
   *  a name. */
  iqama?: string | null;
  holdsVehicleId: string | null; holdsPlate: string | null;
}

/**
 * The mirror of VehicleCell, for screens whose rows are TRUCKS.
 *
 * Same operation asked from the other end - the drivers list gives a vehicle to
 * a person, a fleet list gives a person to a vehicle - and it writes through the
 * same endpoint, so the rule that one truck has one driver is enforced once, in
 * the database, not twice in two components.
 *
 * The option says who the driver is currently on, because assigning them here
 * takes them off that truck and nobody should discover it afterwards.
 */
export function DriverCell({ vehicleId, driverName, canAssign, drivers, onAssign, onUnassign, busy, error }: {
  vehicleId: string;
  driverName: string | null;
  canAssign: boolean;
  drivers: PickableDriver[];
  onAssign: (vehicleId: string, driverId: string) => void;
  onUnassign: (vehicleId: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const [open, setOpen] = useState(false);
  // The driver picked who is already on another truck. Held here, not sent,
  // until it is confirmed: assigning them ENDS that other assignment.
  const [pending, setPending] = useState<PickableDriver | null>(null);

  if (!canAssign) {
    return driverName
      ? <span className="text-sm">{driverName}</span>
      : <span className="text-xs text-muted-foreground italic">No driver</span>;
  }

  if (pending) {
    return (
      <div className="space-y-2 min-w-56 rounded-md border border-amber-500/40 bg-amber-500/5 p-2"
           onClick={(e) => e.stopPropagation()}>
        <p className="text-xs text-foreground">
          <b>{pending.name}</b> is already on <b>{pending.holdsPlate}</b>.
          {' '}Assigning them here takes them off it.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs rounded-md bg-gold/90 hover:bg-gold text-black px-2 py-1"
            onClick={() => { const d = pending; setPending(null); onAssign(vehicleId, d.id); }}
          >
            Move them here
          </button>
          <button
            type="button"
            className="text-xs underline text-muted-foreground hover:text-foreground"
            onClick={() => setPending(null)}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-sm rounded-md border border-transparent hover:border-border px-1.5 py-1 -mx-1.5 transition-colors disabled:opacity-60"
          title={driverName ? 'Change the driver on this truck' : 'Put a driver on this truck'}
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCog className="h-3 w-3 text-muted-foreground" />}
          {driverName ?? <span className="text-muted-foreground italic">Assign driver…</span>}
        </button>
        {error && <div className="text-[11px] text-destructive">{error}</div>}
      </div>
    );
  }

  return (
    <div className="space-y-1 min-w-52" onClick={(e) => e.stopPropagation()}>
      <DriverSelect
        autoFocus
        className="h-8 w-full rounded-md border border-input bg-background px-1.5 text-xs"
        // Not a bound value: this list is an action menu, so it always reopens
        // reading "pick a driver" rather than showing a stale choice.
        value=""
        disabled={busy}
        onChange={(v) => {
          setOpen(false);
          if (v === '__none__') { onUnassign(vehicleId); return; }
          if (!v) return;
          const picked = drivers.find((x) => x.id === v);
          // Ask only when something is actually displaced. A dialog that always
          // appears is one nobody reads.
          if (picked?.holdsVehicleId && picked.holdsVehicleId !== vehicleId) setPending(picked);
          else onAssign(vehicleId, v);
        }}
        aria-label="Assign a driver"
        noneLabel="— pick a driver —"
        extraOptions={driverName ? <option value="__none__">— none (unassign) —</option> : null}
        drivers={drivers.map(toDriverOption)}
        // Which truck they are already on is the reason this warns before it
        // moves anyone, so it stays on the option itself.
        renderLabel={(d) =>
          `${d.name}${d.iqama ? ` · ${d.iqama}` : ''}` +
          (d.holdsVehicleId && d.holdsVehicleId !== vehicleId ? ` · on ${d.holdsPlate}` : '')
        }
      />
      <button type="button" className="text-[11px] underline text-muted-foreground hover:text-foreground"
        onClick={() => setOpen(false)}>
        cancel
      </button>
    </div>
  );
}
